import "@fontsource/rubik";
import "@fontsource/rubik/600.css";
import "@fontsource/rubik/700.css";
import { tooltipClasses } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import colors from "./colors";

declare module "@mui/material/Chip" {
  interface ChipPropsSizeOverrides {
    tiny: true;
    xxs: true;
  }
}

declare module "@mui/material/ListSubheader" {
  interface ListSubheaderOwnProps {
    disableDivider?: boolean;
  }
}

const rootElement = document.getElementById("root");

const bgColor = "#F8F9FC"; // Also in tailwind as bg-color-bg
const tailwindTextGray500 = "rgb(var(--color-gray-500-rgb, 107 114 128) / 1)";

export let theme = createTheme({
  // https://zenoo.github.io/mui-theme-creator/
  shape: {
    borderRadius: 6,
  },
  palette: {
    primary: {
      main: "#074EE8",
    },
    success: {
      main: "#22c55e",
      contrastText: "#ffffff",
    },
  },
  typography: {
    // In Chinese and Japanese the characters are usually larger,
    // so a smaller fontsize may be appropriate.
    fontFamily: ["Rubik", "sans-serif"].join(","),
    fontSize: 14,
    h1: {
      fontSize: "2rem",
      fontWeight: 500,
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 500,
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
    },
  },
});

theme = createTheme(theme, {
  components: {
    MuiRadio: {
      styleOverrides: {
        root: {
          borderLeft: "4px solid transparent",
          borderRight: "4px solid transparent",
          padding: 5,
          backgroundClip: "padding-box",
          // Keep the input clickable across the horizontal transparent border.
          "& input": {
            left: -4,
            width: "calc(100% + 8px)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: tailwindTextGray500,
          "& .MuiSvgIcon-root": {
            color: "inherit",
          },
        },
        colorPrimary: {
          color: theme.palette.primary.main,
        },
        sizeSmall: {
          "& .MuiSvgIcon-root": {
            fontSize: "16px",
          },
        },
      },
    },
    MuiModal: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: ["Rubik", "sans-serif"].join(","),
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: bgColor,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: bgColor,
          // backgroundColor: theme.palette.primary.main,
          color: colors.primary.contrastText,
          boxShadow: "none",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider,
          borderBottomWidth: 1,
          borderBottomStyle: "solid",
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          strokeLinecap: "round",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: theme.shape.borderRadius,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: "outlined", color: "inherit" },
          style: {
            borderColor: "rgb(209, 213, 219)",
            color: "rgba(0, 0, 0, 0.87)",
            backgroundColor: "rgb(251, 251, 251)",
            "&:hover": {
              backgroundColor: "rgb(244 244 245)",
            },
            "& .MuiSvgIcon-root:hover": {
              backgroundColor: "rgb(244 244 245)",
            },
          },
        },
      ],
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          elevation: 0,
          border: "none",
          backgroundColor: bgColor,
          flex: 1,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "filled",
      },
      styleOverrides: {
        root: {},
      },
    },

    MuiSkeleton: {
      styleOverrides: {
        root: {
          transform: "none",
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        slotProps: {
          popper: {
            sx: {
              [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                {
                  marginTop: "6px",
                },
              [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                {
                  marginBottom: "6px",
                },
              [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                {
                  marginLeft: "6px",
                },
              [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                {
                  marginRight: "6px",
                },
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider,
          "&:hover": {
            backgroundColor: theme.palette.grey[50],
            borderColor: theme.palette.divider,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          boxSizing: "border-box",
          "& .MuiSelect-multiple": {
            zIndex: 10,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            backgroundColor: theme.palette.grey[50],
            borderColor: theme.palette.divider,
            borderWidth: 1,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            backgroundColor: "white",
          },
          "&:hover": {
            borderColor: "inherit",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
          },
          "& .MuiSelect-icon": {
            fill: theme.palette.grey[600],
            zIndex: 10,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          borderRadius: 4,
          borderWidth: 1,
          borderColor: theme.palette.divider,
          borderStyle: "solid",
          "&:hover": {
            backgroundColor: theme.palette.grey[50],
            borderColor: theme.palette.divider,
          },
          "&.Mui-disabled": {
            backgroundColor: theme.palette.grey[50],
          },
          "&::before": {
            borderBottom: "none",
          },
          "&.Mui-disabled::before": {
            borderBottom: "none",
          },
          "&:hover:not(.Mui-disabled, .Mui-error):before": {
            borderBottom: "none",
          },
          "&.Mui-focused:after": {
            borderBottom: "none",
          },
          "&.Mui-focused": {
            borderWidth: 1,
            borderColor: theme.palette.primary.main,
            borderStyle: "solid",
            backgroundColor: "#ffffff",
          },
          // Override hover background when focused
          "&.Mui-focused:hover": {
            backgroundColor: "#ffffff",
          },
        },
        input: {
          // Ensure input element itself has transparent background
          backgroundColor: "transparent",
          "&:focus": {
            backgroundColor: "transparent",
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          "& .MuiBreadcrumbs-li": {
            color: theme.palette.text.primary,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderBottom: "none",
          },
          "& .MuiInputBase-root::before": {
            borderBottom: "none",
          },
          "& .MuiInputBase-root:not(.MuiInputBase-hiddenLabel)": {
            paddingTop: "11px",
          },
          "&:hover .MuiInputBase-root::before": {
            borderBottom: "none !important",
          },
          "& .MuiInputBase-root::after": {
            borderBottom: "none",
          },
          "& .MuiInputBase-input:not(.MuiInputBase-inputHiddenLabel)": {
            padding: "4px",
          },
          "& .MuiInputBase-input.MuiFilledInput-input:not(.MuiInputBase-inputHiddenLabel)":
            {
              padding: "6px 4px",
            },
          "& .MuiInputBase-input.MuiFilledInput-input.MuiInputBase-inputAdornedEnd:not(.MuiInputBase-inputHiddenLabel)":
            {
              paddingTop: "8px",
              paddingBottom: "4px",
            },
          "& .MuiFormLabel-root.MuiInputLabel-root": {
            transform: "translate(12px, 12px) scale(1)",
          },
          "& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-shrink": {
            transform: "translate(12px, 3px) scale(0.75)",
          },
          "& .MuiInputBase-sizeSmall .MuiFormLabel-root.MuiInputLabel-root, & .MuiInputBase-sizeSmall + .MuiFormLabel-root.MuiInputLabel-root":
            {
              transform: "translate(12px, 8px) scale(1)",
            },
          "& .MuiInputBase-sizeSmall .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-shrink, & .MuiInputBase-sizeSmall + .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-shrink":
            {
              transform: "translate(12px, 1px) scale(0.75)",
            },
          "& .MuiInputBase-root.MuiInputBase-sizeSmall:not(.MuiInputBase-hiddenLabel)":
            {
              paddingTop: "7px",
            },
          "& + .MuiAutocomplete-popper .MuiAutocomplete-option": {
            margin: "0 6px",
            borderRadius: "4px",
            transition: "background-color 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            background: "#FBFBFB",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          "&.MuiChip-sizeSmall": {
            height: "auto",
          },
          variants: [
            {
              props: { size: "tiny" },
              style: {
                height: "auto",
                "& .MuiChip-label": {
                  fontSize: "0.75rem",
                  lineHeight: "1.5em",
                  //padding: "2px 6px",
                },
              },
            },
          ],
        },
        // label: {
        //   padding: "0",
        // },
      },
      variants: [
        {
          props: { size: "xxs" },
          style: {
            height: 20,
            fontSize: 10,
            "& .MuiChip-icon": {
              fontSize: 10,
              height: 10,
              marginLeft: 6,
              marginRight: -2,
              width: 10,
            },
            "& .MuiChip-label": {
              fontSize: 10,
              paddingLeft: 6,
              paddingRight: 6,
            },
          },
        },
        {
          props: { variant: "tiny" },
          style: {
            height: "auto",
            "& .MuiChip-label": {
              fontSize: "0.75rem",
              padding: "",
            },
          },
        },
      ],
    },
    MuiBadge: {
      styleOverrides: {
        root: {
          "& .MuiBadge-badge.MuiBadge-standard": {
            border: `2px solid ${theme.palette.background.paper}`,
            padding: "0 4px",
          },
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          "& > .MuiPaper-root": {
            minWidth: "fit-content",
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: "2px",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          padding: "6px 10px",
          margin: "4px 6px",
          borderRadius: "4px",
          transition: "background-color 0.2s ease-in-out",
        },
        hover: {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          lineHeight: "1.6em",
          fontSize: theme.typography.body1.fontSize,
          fontWeight: theme.typography.fontWeightRegular,
          color: theme.palette.text.secondary,
          paddingBottom: theme.spacing(1),
          marginBottom: theme.spacing(1),
          borderBottom: `1px solid ${theme.palette.divider}`,
          "* + &": {
            borderTop: `1px solid ${theme.palette.divider}`,
            marginTop: theme.spacing(1),
            paddingTop: theme.spacing(1),
          },
        },
      },
      variants: [
        {
          props: { disableDivider: true },
          style: {
            borderBottom: "none",
            marginBottom: 0,
            paddingBottom: 0,
            "* + &": {
              borderTop: "none",
              marginTop: 0,
              paddingTop: 0,
            },
          },
        },
      ],
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: "6px 10px",
          margin: "4px 6px",
          borderRadius: "4px",
          transition: "background-color 0.2s ease-in-out",
        },
        hover: {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "8px 12px",
          borderBottom: `1px solid ${theme.palette.divider}`,
          marginBottom: "14px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${theme.palette.divider}`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingLeft: "24px",
          paddingRight: "24px",
          paddingBottom: "14px",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          alignSelf: "baseline",
          width: "100%",
          backgroundColor: "white",
          "&.MuiAccordion-root": {
            boxShadow: "none",
            margin: "0",
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: "16px",
          "&:hover": {
            "& #title": {
              textDecoration: "underline",
            },
          },
          backgroundColor: "white",
          "& .MuiAccordionSummary-content": {
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: "column",
            margin: "0",
          },
          "& .MuiAccordionSummary-content.Mui-expanded": {
            margin: "0",
          },
        },
      },
    },
  },
});
